import { c as createFocusTrap } from './focus-trap.esm-D3-4vFhC.js';
import { onClickOutside } from './utils-on-click-outside.js';

class DropdownElement extends HTMLElement {
  static {
    // Use mousedown to prevent click conflicts
    // We'll handle the toggling from the header-element.js instead
    // Keeping this as a fallback for other places in the site
    document.addEventListener('click', (event) => {
      // Skip if we're in the header navigation - this is handled by header-element.js
      if (event.target.closest('[data-nav-primary]')) return;

      const controller = event.target.closest('[data-dropdown-controller]');
      const dropdownId = controller?.getAttribute('aria-controls');
      if (!dropdownId) return;

      const dropdown = document.getElementById(dropdownId);
      if (!dropdown) {
        console.error(`Dropdown with id '${dropdownId}' not found`);
        return;
      }
      dropdown.handleAction('toggle');
      event.preventDefault();
    });
  }

  connectedCallback() {
    // Get transition duration from data attribute (set by Tailwind)
    this.transitionDuration = parseInt(this.dataset.transitionDuration || '150');

    // Create the focus trap
    this.trap = createFocusTrap(this, {
      // Allow clicks outside the modal to close the dropdown
      allowOutsideClick: true,
      initialFocus: false,
    });

    // Listen for key:escape events
    this.unsubscribeEscapeKey = subscribe('key:escape', () => this.close());

    // Disable onClickOutside for menu dropdowns to prevent conflicts
    // We're handling this in header-element.js with mousedown event
    if (!this.closest('[data-nav-primary]')) {
      this.unsubscribeClickOutside = onClickOutside(this, () => this.close());
    }

    // Listen for transition end to clean up after animations
    this.addEventListener('transitionend', (event) => {
      // Only react to opacity transitions to avoid multiple calls
      if (event.propertyName === 'opacity' && this.ariaHidden === 'true') {
        this.dispatchEvent(new CustomEvent('dropdown:closed'));
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey?.();
    this.unsubscribeClickOutside?.();
  }

  /**
   * Handle dropdown actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      default:
        this.toggle();
        break;
    }
  }

  /**
   * Toggle the dropdown
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Open the dropdown
   */
  open() {
    if (this.ariaHidden === 'false') return;

    // Set aria-hidden to false immediately to start animation
    this.ariaHidden = false;
    this.updateControllers();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('dropdown:opened'));

    // Check if there are any tabbable elements before activating the focus trap
    const tabbableElements = this.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    setTimeout(() => {
      if (tabbableElements.length > 0) {
        this.trap.activate();
      }
    }, this.transitionDuration);
  }

  /**
   * Close the dropdown
   */
  close() {
    if (this.ariaHidden === 'true') return;

    // Deactivate the focus trap immediately
    this.trap.deactivate();

    // Start the closing animation by setting aria-hidden
    this.ariaHidden = true;
    this.updateControllers();
    this.closeBackdrop();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('dropdown:closing'));
  }

  /**
   * Update the aria-expanded attribute on dropdown controllers
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }

  /**
   * Close the backdrop
   */
  closeBackdrop() {
    const backdrop = document.querySelector('[data-backdrop-menu]');
    if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
  }
}

customElements.define('dropdown-element', DropdownElement);

export { DropdownElement };
